import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module';
import { type GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { SCENE, Positions } from './Config';
const ORIGIN = new THREE.Vector3();
import * as THREE from 'three';
import RAF from '@/utils/RAF';
import { isLocalDev } from '@/utils/env';

export default class Scene {
  private renderer!: THREE.WebGLRenderer;
  private readonly box = new THREE.Box3();
  private camera!: THREE.PerspectiveCamera;
  private readonly vec = new THREE.Vector3();

  private readonly clock = new THREE.Clock();
  private readonly scene = new THREE.Scene();
  private readonly loader = new GLTFLoader();

  private readonly names: THREE.Vector2[] = [];
  private readonly update = this.render.bind(this);
  private readonly mixers: THREE.AnimationMixer[] = [];

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.loader.setMeshoptDecoder(MeshoptDecoder);
    console.info('Scene.constructor');
    this.createRenderer();
    this.createCamera();
    this.createLights();

    RAF.add(this.update);
    RAF.pause = false;
  }

  private createRenderer(): void {
    this.renderer = new THREE.WebGLRenderer({
      powerPreference: 'high-performance',
      canvas: this.canvas,
      antialias: true,
      alpha: true
    });

    this.renderer.debug.checkShaderErrors = isLocalDev();
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMappingExposure = 1.2;

    this.renderer.setClearColor(new THREE.Color(), 0);
    this.renderer.setSize(innerWidth, innerHeight);
    this.renderer.setPixelRatio(devicePixelRatio);
  }

  private createCamera(): void {
    const { camera } = SCENE;

    this.camera = new THREE.PerspectiveCamera(
      camera.fov,
      innerWidth / innerHeight,
      camera.near,
      camera.far
    );

    this.camera.position.set(
      camera.position.x,
      camera.position.y,
      camera.position.z
    );

    this.camera.lookAt(camera.lookAt.oneRow);
  }

  private createLights(): void {
    const { lights } = SCENE;

    this.scene.add(
      new THREE.AmbientLight(
        new THREE.Color(lights.ambient.color),
        lights.ambient.intensity
      )
    );

    const left = new THREE.DirectionalLight(
      new THREE.Color(lights.directional.left.color),
      lights.directional.left.intensity
    );
    const right = new THREE.DirectionalLight(
      new THREE.Color(lights.directional.right.color),
      lights.directional.right.intensity
    );

    left.position.copy(lights.directional.left.position);
    right.position.copy(lights.directional.right.position);

    this.scene.add(left, right);

    right.lookAt(ORIGIN);
    left.lookAt(ORIGIN);
  }

  public addAvatar(
    avatar: GLTF,
    age: string,
    index: number,
    avatars: number
  ): void {
    const smallFamily = avatars < 5;
    const { twoRows } = SCENE.camera.lookAt;

    !smallFamily && this.camera.lookAt(twoRows);
    const { base, child, teen, adult } = SCENE.scale;

    const position = Positions.get(avatars)?.[index] ?? ORIGIN;
    const { clientWidth, clientHeight } = this.renderer.domElement;

    const scale = age === 'Child' ? child : age === 'Teen' ? teen : adult;
    const animation = avatar.animations.find(({ name }) => name === 'IDLE');

    if (animation) {
      const mixer = new THREE.AnimationMixer(avatar.scene);
      mixer.clipAction(animation).setLoop(THREE.LoopRepeat, Infinity).play();
      mixer.setTime(Math.random() * animation.duration);
      this.mixers.push(mixer);
    }

    console.info(`Scene.addAvatar ${index}/${avatars}; ${age}yo.`);
    avatar.scene.scale.setScalar(base * scale);
    avatar.scene.position.copy(position);
    this.scene.add(avatar.scene);

    // Calculate name label position:
    this.box.setFromObject(avatar.scene);
    const height = this.box.max.y - this.box.min.y;
    const offset = (smallFamily && height + 2) || height * 0.55;

    this.vec.copy(position);
    this.vec.y = position.y + offset;
    this.vec.project(this.camera);

    // Set label's position based on the amount of avatars:
    const y = 18 - +(avatars > 4) * 25;

    this.names.push(
      new THREE.Vector2(
        (this.vec.x * 0.5 + 0.5) * clientWidth,
        Math.min(-this.vec.y * 0.5 + 0.5, 0.33) * clientHeight + y
      )
    );
  }

  private render(): void {
    const delta = this.clock.getDelta();
    this.mixers.forEach((mixer) => mixer.update(delta));
    this.renderer.render(this.scene, this.camera);
  }

  private disposeMaterial(material: THREE.Material): void {
    (material as THREE.MeshStandardMaterial).displacementMap?.dispose();
    (material as THREE.MeshStandardMaterial).metalnessMap?.dispose();
    (material as THREE.MeshStandardMaterial).roughnessMap?.dispose();

    (material as THREE.MeshStandardMaterial).emissiveMap?.dispose();
    (material as THREE.MeshStandardMaterial).normalMap?.dispose();
    (material as THREE.MeshStandardMaterial).alphaMap?.dispose();

    (material as THREE.MeshStandardMaterial).lightMap?.dispose();
    (material as THREE.MeshBasicMaterial).specularMap?.dispose();
    (material as THREE.MeshToonMaterial).gradientMap?.dispose();

    (material as THREE.MeshStandardMaterial).bumpMap?.dispose();
    (material as THREE.MeshStandardMaterial).envMap?.dispose();
    (material as THREE.MeshStandardMaterial).aoMap?.dispose();
    (material as THREE.MeshStandardMaterial).map?.dispose();

    material.dispose();
  }

  private disposeElement(child: THREE.Object3D): void {
    if (child instanceof THREE.Mesh) {
      const mesh = child as THREE.Mesh;
      mesh.geometry.dispose();

      !Array.isArray(mesh.material)
        ? this.disposeMaterial(mesh.material)
        : mesh.material.forEach(this.disposeMaterial);
    }
  }

  public dispose(): void {
    this.scene.traverse(this.disposeElement.bind(this));
    this.loader.setMeshoptDecoder(null);
    this.renderer.domElement.remove();

    RAF.remove(this.update);
    this.renderer.dispose();
    this.mixers.splice(0);
    this.scene.clear();
  }

  public get labels(): THREE.Vector2[] {
    return this.names;
  }
}
